import { createFileRoute } from "@tanstack/react-router";
import { doc, serverTimestamp, updateDoc, type Timestamp } from "firebase/firestore";
import { CalendarClock, FileText, Inbox, Loader2, MapPin, RotateCcw } from "lucide-react";
import { useState, type ReactNode } from "react";
import { toast } from "sonner";

import { useConfirm } from "@/components/DialogProvider";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useProjectStatuses } from "@/features/config/hooks/useConfig";
import { db } from "@/firebase";
import type { ProjectModel } from "@/firestore_types";
import { getJobLocationAddress, getJobLocationNotes } from "@/lib/jobLocation";
import { useDeleteProjects } from "../-admin-context";

export const Route = createFileRoute("/dashboard/admin/trash/")({
  component: RouteComponent,
});

function RouteComponent() {
  const result = useDeleteProjects();

  const deletedProjects = result.asData ?? [];

  return (
    <div className="flex flex-col gap-6">
      {deletedProjects.length === 0 ? (
        <EmptyState />
      ) : (
        deletedProjects.map((project) => <ProjectCard key={project.id} project={project} />)
      )}
    </div>
  );
}

interface ProjectCardProps {
  project: ProjectModel;
}

function ProjectCard({ project }: ProjectCardProps) {
  const { id, name, description, statusId, jobLocation, startDate, endDate, deletedOn } = project;
  const { projectStatuses } = useProjectStatuses();
  const status = projectStatuses.find((s) => s.id === statusId);

  const start = formatDateLabel(startDate);
  const end = formatDateLabel(endDate);
  const addressNotes = getJobLocationNotes(jobLocation);
  const deletedDate = formatDateLabel(deletedOn ?? null);

  return (
    <Card className="border-muted/60">
      <CardHeader className="gap-4 md:flex md:items-center md:justify-between">
        <div className="space-y-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-lg">
            {name}
            {status ? <StatusBadge label={status.name} color={status.color} /> : null}
          </CardTitle>
          {description ? (
            <CardDescription className="line-clamp-2 text-sm leading-relaxed">
              {description}
            </CardDescription>
          ) : null}
        </div>
        <CardAction>
          <RestoreProjectButton projectId={id} projectName={name} />
        </CardAction>
      </CardHeader>
      <CardContent className="grid gap-4 md:grid-cols-3">
        <InfoBlock
          icon={<MapPin className="text-muted-foreground size-4" />}
          label="Job location"
          value={getJobLocationAddress(jobLocation) || "Not provided"}
        />
        {addressNotes ? (
          <InfoBlock
            icon={<FileText className="text-muted-foreground size-4" />}
            label="Address notes"
            value={addressNotes}
          />
        ) : null}
        <InfoBlock
          icon={<CalendarClock className="text-muted-foreground size-4" />}
          label="Start date"
          value={start ?? "Not scheduled"}
        />
        <InfoBlock
          icon={<CalendarClock className="text-muted-foreground size-4" />}
          label="End date"
          value={end ?? "Not scheduled"}
        />
        <InfoBlock
          icon={<CalendarClock className="text-muted-foreground size-4" />}
          label="Deleted on"
          value={deletedDate ?? "Unknown"}
        />
      </CardContent>
      <CardFooter className="text-muted-foreground flex-col items-start gap-2 text-xs md:flex-row md:items-center md:justify-between">
        <p>Restoring a project will make it visible to admins and managers again.</p>
      </CardFooter>
    </Card>
  );
}

interface InfoBlockProps {
  icon: ReactNode;
  label: string;
  value: string;
}

function InfoBlock({ icon, label, value }: InfoBlockProps) {
  return (
    <div className="border-muted flex flex-col gap-1 rounded-lg border border-dashed p-4">
      <span className="text-muted-foreground flex items-center gap-2 text-xs font-medium tracking-wide uppercase">
        {icon}
        {label}
      </span>
      <span className="text-foreground text-sm font-medium">{value}</span>
    </div>
  );
}

interface StatusBadgeProps {
  label: string;
  color?: string;
}

function StatusBadge({ label, color }: StatusBadgeProps) {
  return (
    <Badge
      variant="secondary"
      className="bg-muted text-foreground border-transparent"
      style={color ? { borderColor: color, color } : undefined}
    >
      {label}
    </Badge>
  );
}

interface RestoreProjectButtonProps {
  projectId: string;
  projectName: string;
}

function RestoreProjectButton({ projectId, projectName }: RestoreProjectButtonProps) {
  const [isRestoring, setIsRestoring] = useState(false);

  const confirm = useConfirm();

  const handleRestore = async () => {
    const confirmed = await confirm({
      title: "Restore project",
      description: `"${projectName}" will reappear everywhere active projects are shown.`,
    });

    if (!confirmed) return;

    setIsRestoring(true);
    try {
      const projectRef = doc(db, "projects", projectId);
      await updateDoc(projectRef, {
        isDeleted: false,
        deletedOn: null,
        updatedAt: serverTimestamp(),
      });
      toast.success(`Restored "${projectName}"`);
    } catch (error) {
      console.error("Failed to restore project", error);
      toast.error("Something went wrong while restoring the project.");
    } finally {
      setIsRestoring(false);
    }
  };

  return (
    <Button onClick={handleRestore} size="sm" variant="outline" disabled={isRestoring}>
      {isRestoring ? (
        <>
          <Loader2 className="size-4 animate-spin" />
          Restoring...
        </>
      ) : (
        <>
          <RotateCcw className="size-4" />
          Restore
        </>
      )}
    </Button>
  );
}

function EmptyState() {
  return (
    <div className="border-muted bg-muted/30 flex flex-col items-center justify-center gap-3 rounded-xl border border-dashed px-8 py-16 text-center">
      <Inbox className="text-muted-foreground size-10" />
      <div className="space-y-1">
        <p className="text-foreground text-base font-semibold">No deleted projects</p>
        <p className="text-muted-foreground text-sm">
          Deleted projects will appear here for quick recovery.
        </p>
      </div>
    </div>
  );
}

function formatDateLabel(value: Timestamp | null) {
  if (!value) return null;
  const date = value.toDate();
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
